import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Menu, MenuItem, Typography } from '@mui/material';

import { IconButton, MoreVertIcon } from '@occc-app/design-system';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { RemoveLocationsFromRetailerNetworkLocationGroup } from '@occc-app/App/PolicyBasedAccessControl/userActions';
import { RetailerNetworkLocationFragment } from '@occc/graphql';

import {
  StoreConnectionStatusChip,
  getLocationConnectionStatus,
} from './store-connection-status-chip.component';
import { StoreMapThumbnail } from './store-map-thumbnail.component';

import { RetailerNetworkLocationGroupRemoveLocationDialog } from '../../retailer-network-location-group-details-page/components/retailer-network-location-group-remove-location-dialog.component';
import { routeCreator } from '../../retailer-connect.utils';

const MAP_THUMBNAIL_HEIGHT = 90;

type StoreCardProps = {
  location: RetailerNetworkLocationFragment;
  networkId: string;
  locationGroupId?: string;
  locationGroupName?: string;
  onRemoveFromLocationGroup?: (locationUuid: string) => Promise<void>;
  isRemoveInProgress?: boolean;
};

export const StoreCard = ({
  location,
  networkId,
  locationGroupId,
  locationGroupName,
  onRemoveFromLocationGroup,
  isRemoveInProgress,
}: StoreCardProps) => {
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const loc = location.location;
  const address = loc.address;
  const status = getLocationConnectionStatus(location.isOnline);
  const cityStateZip = [address?.city, address?.state, address?.zipCode].filter(Boolean).join(', ');
  const fullAddress = [address?.street1, cityStateZip].filter(Boolean).join('\n');

  const canRemove = Boolean(locationGroupId && onRemoveFromLocationGroup);
  // Populated by the BE for any location that belongs to a location group, regardless of which
  // view/filter surfaced it (confirmed against a live retailerNetworkLocations response with
  // stateEquals/cityEquals filters) — unlike the locationGroupId prop, which is only set when a
  // card happens to be rendered inside its own group's section (grouped view only).
  const locationGroupUuid = location.userAssociationInheritedFromLocationGroup;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setMenuAnchorEl(null);

  const handleViewLocationDetails = () => {
    closeMenu();
    const groupSuffix =
      locationGroupId && locationGroupName
        ? `?groupRef=${locationGroupId}&groupName=${encodeURIComponent(locationGroupName)}`
        : '';
    navigate(routeCreator.locationDetails(networkId, location.uuid) + groupSuffix);
  };

  const handleViewLocationGroupDetails = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    closeMenu();
    if (locationGroupUuid) {
      navigate(routeCreator.locationGroupDetails(networkId, locationGroupUuid));
    }
  };

  const handleRemoveClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    closeMenu();
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    await onRemoveFromLocationGroup?.(location.uuid);
    setRemoveDialogOpen(false);
  };

  return (
    <>
      <Card
        variant='outlined'
        elevation={0}
        role='button'
        tabIndex={0}
        onClick={() => {
          if (!menuAnchorEl) handleViewLocationDetails();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleViewLocationDetails();
          }
        }}
        sx={{
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <StoreMapThumbnail location={location} height={MAP_THUMBNAIL_HEIGHT} />

        <CardContent sx={{ p: 2, pb: '16px !important' }}>
          <Box display='flex' flexDirection='column' gap={0.75}>
            <Typography
              variant='body1'
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 500,
                minHeight: '1.5em',
              }}
            >
              {loc.name}
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                opacity: 0.6,
                whiteSpace: 'pre-line',
                minHeight: '2.4em',
                maxHeight: '2.4em',
                overflow: 'hidden',
              }}
            >
              {fullAddress}
            </Typography>
            <StoreConnectionStatusChip status={status} />
          </Box>
        </CardContent>

        <Box sx={{ position: 'absolute', bottom: 12, right: 8 }}>
          <IconButton
            size='small'
            aria-label='Store actions'
            onClick={handleMenuOpen}
            sx={{ p: 0 }}
          >
            <MoreVertIcon fontSize='small' />
          </IconButton>
          <Menu
            open={Boolean(menuAnchorEl)}
            anchorEl={menuAnchorEl}
            onClose={closeMenu}
            PaperProps={{ sx: { borderRadius: 1 } }}
          >
            <MenuItem onClick={handleViewLocationDetails}>View Location Details</MenuItem>
            {locationGroupUuid && (
              <MenuItem onClick={handleViewLocationGroupDetails}>
                View Location Group Details
              </MenuItem>
            )}
            {canRemove && (
              <Can I={RemoveLocationsFromRetailerNetworkLocationGroup({ uuid: networkId })}>
                <MenuItem onClick={handleRemoveClick}>Remove Store From Group</MenuItem>
              </Can>
            )}
          </Menu>
        </Box>
      </Card>

      {canRemove && (
        <RetailerNetworkLocationGroupRemoveLocationDialog
          open={removeDialogOpen}
          setOpen={setRemoveDialogOpen}
          onConfirm={handleConfirmRemove}
          loading={isRemoveInProgress}
        />
      )}
    </>
  );
};
